import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { getEventDraftById, activateEventBySlug, getAllSponsorTypes } from '../../api';
import './EventPreviewPage.css';
import { getOrganizerCurrency } from '../../utils/currency';
import Background from '../Login/Background';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import { Eye, Pencil, Globe, Check, Calendar as CalendarIcon, Clock, MapPin, Ticket, Users, Tag, Handshake, Music, ExternalLink, Layers, Sparkles, Image as ImageIcon, ArrowLeft } from 'lucide-react';

interface EventData {
  event: {
    eventId: string;
    slugId: string;
    eventName: string;
    eventDate: string;
    endDate?: string;
    isSingleDayEvent?: boolean;
    eventTime: string;
    endTime: string;
    eventDuration: string;
    thumbImg?: string;
    bannerImgDesktop?: string;
    bannerImgMobile?: string;
    eventDescription: string;
    shortDescription: string;
    eventLocation: string;
    eventVenue: string;
    eventAddress1: string;
    eventAddress2: string;
    eventCountry: string;
    eventState: string;
    eventCity: string;
    eventZip: string;
    eventType: string;
    onlineEventType: string;
    eventStatus: string;
    hasMultipleVenues: boolean;
    hasMultipleTicketGroups: boolean;
    absorbFees: string;
    showOtherFees: boolean;
    isOtherAttendeeRequired: boolean;
    hasTermsAndConditions: boolean;
    termsAndConditions: string;
    templateName: string;
    processingFee: number;
    ticketFee: number;
    platformFee: number;
    categoryId?: string;
  };
  venues: Array<{
    venueId: string;
    venueName: string;
    venueAddress: string;
    eventSlug: string;
    eventDate: string;
    status: string;
  }>;
  ticketGroups: Array<{
    id: number;
    groupName: string;
    slugId: string;
    status: string;
    isSlotRequired: boolean;
  }>;
  ticketTypes: Array<{
    ticketTypeId: string;
    ticketType: string;
    price: number;
    maxCount: string;
    ticketStatus: string;
    currentCount: string;
    eventSlug: string;
    ticketOrder: string;
    venueId: string;
    groupTicketPrice: number | null;
    isGroupTicket: boolean;
    groupTicketCount: number | null;
    groupingId: string;
    description: string;
    isOfferActive?: boolean;
    previousAmount?: number;
  }>;
  agendas: Array<{
    agendaId: string;
    hostImage: string;
    startTime: string;
    endTime: string;
    title: string;
    description: string;
    eventId: string;
    organizerId: string;
    agendaDate: string;
    createdDate: string;
    sortOrder: number;
    isActive: boolean;
    hostName: string;
    location: string | null;
  }>;
  lineups: Array<{
    lineupId: string;
    artistImage: string;
    name: string;
    tagLine: string;
    description: string;
    eventId: string;
    socialMediaLine: string | null;
    createdDate: string;
    organizerId: string;
    sortOrder: number;
    isActive: boolean;
  }>;
  sponsors: Array<{
    sponsorId: string;
    name: string;
    link: string | null;
    thumb: string;
    sponsorTypeId: string;
    eventSlug: string;
  }>;
  files: any[];
}

const EventPreviewPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const currencySymbol = getOrganizerCurrency().symbol;
  const fromAskEv = Boolean((location.state as { fromAskEv?: boolean } | null)?.fromAskEv);
  const returnToAskEv = () => {
    const path = (location.state as { returnTo?: string } | null)?.returnTo || '/ai';
    navigate(path);
  };

  const [eventData, setEventData] = useState<EventData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showAllAgendas, setShowAllAgendas] = useState(false);
  const [showAllLineups, setShowAllLineups] = useState(false);
  const [showAllSponsors, setShowAllSponsors] = useState(false);
  const [sponsorTypes, setSponsorTypes] = useState<{ sponsorTypeId: string; title: string }[]>([]);

  useEffect(() => {
    const fetchSponsorTypes = async () => {
      try {
        const response = await getAllSponsorTypes();
        setSponsorTypes(response);
      } catch (err) {
        console.error('Failed to fetch sponsor types', err);
      }
    };
    fetchSponsorTypes();
  }, []);

  const getSponsorTypeTitle = (typeId: any) => {
    const type = sponsorTypes.find(t => t.sponsorTypeId === typeId.toString());
    return type ? type.title : 'Unknown Type';
  };

  useEffect(() => {
    const fetchEventData = async () => {
      if (!eventId) {
        setError('Event ID is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await getEventDraftById(eventId);
        // Allow all organizer event statuses so Ask Ev (and Events) can preview
        // inactive/closed as well as draft and live (active).
        const status = String(response?.data?.event?.eventStatus || '').toLowerCase();
        const previewable = ['draft', 'active', 'inactive', 'closed'];
        if (previewable.includes(status)) {
          setEventData(response?.data);
        } else {
          setEventData(null);
          setError('Event not found or not available for preview.');
        }
      } catch (err: any) {
        console.error('Error fetching event data:', err);
        setError(err?.response?.data?.error || 'Failed to fetch event data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventData();
  }, [eventId]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTicketPriceText = (ticketTypes: any[]) => {
    if (!ticketTypes || ticketTypes.length === 0) return 'No tickets available';
    const prices = ticketTypes.map(t => t.price).filter((p: number) => p !== undefined);
    if (prices.length === 0) return 'Free';
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    if (minPrice === maxPrice) {
      return minPrice === 0 ? 'Free' : `${currencySymbol}${minPrice.toFixed(2)}`;
    }
    return `${currencySymbol}${minPrice.toFixed(2)} - ${currencySymbol}${maxPrice.toFixed(2)}`;
  };

  const getTotalCapacity = (ticketTypes: any[]) => {
    if (!ticketTypes || ticketTypes.length === 0) return 'Unlimited';
    const total = ticketTypes.reduce((sum: number, ticket: any) => {
      const maxCount = parseInt(ticket.maxCount) || 0;
      if (maxCount === -1 || maxCount === 0) return sum;
      const effective = ticket.isGroupTicket && ticket.groupTicketCount
        ? maxCount * parseInt(ticket.groupTicketCount)
        : maxCount;
      return sum + effective;
    }, 0);
    return total === 0 ? 'Unlimited' : total.toString();
  };

  const getTicketTypeText = (type: string) => (type === 'group' ? 'Group Ticket' : 'Single Ticket');

  const getTicketGroupName = (ticketGroupId: string | number) => {
    if (!eventData?.ticketGroups) return '';
    const ticketGroup = eventData.ticketGroups.find(tg => String(tg.id) === String(ticketGroupId));
    return ticketGroup ? ticketGroup.groupName : '';
  };

  const getVenueName = (venueId: string | number) => {
    if (!eventData?.venues) return '';
    const venue = eventData.venues.find(v => String(v.venueId) === String(venueId));
    return venue ? venue.venueName : '';
  };

  const getMaxCountText = (ticket: { maxCount: string | null; isGroupTicket?: boolean; groupTicketCount?: number | string | null }) => {
    const { maxCount, isGroupTicket, groupTicketCount } = ticket;
    if (maxCount === null || maxCount === '-1' || maxCount === '') return 'Unlimited';
    if (isGroupTicket && groupTicketCount) {
      return (parseInt(maxCount) * parseInt(groupTicketCount as string)).toString();
    }
    return maxCount;
  };

  const getFormattedDateOnly = () => {
    if (!eventData) return '';
    return formatDate(eventData.event.eventDate);
  };

  const getFormattedTimeOnly = () => {
    if (!eventData) return '';
    const { eventTime, endTime } = eventData.event;
    const formatTo12h = (t: string) => {
      if (!t) return '';
      const parts = t.split(':');
      if (parts.length < 2) return t;
      const [h, m] = parts.map(Number);
      if (isNaN(h) || isNaN(m)) return t;
      return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
    };
    const start = formatTo12h(eventTime);
    const end = formatTo12h(endTime);
    return end ? `${start} - ${end}` : start;
  };

  const handleBackToEdit = () => {
    if (eventId) navigate(`/edit-quick-event-v2/${eventId}`);
  };

  const handlePublish = async () => {
    setSuccess('');
    setError('');

    const slug = eventData?.event?.slugId;
    const eventId = eventData?.event?.eventId;
    if (!slug) {
      setError('Missing event slug. Cannot publish.');
      return;
    }

    try {
      setIsPublishing(true);
      await activateEventBySlug(slug);
      setEventData(prev => (prev ? { ...prev, event: { ...prev.event, eventStatus: 'active' } } : prev));
      setSuccess('Event published successfully.');
      
      if (eventData) {
        const storedEventData = {
          eventId: eventData.event.eventId,
          slugId: eventData.event.slugId,
          eventName: eventData.event.eventName,
          ticketFee: eventData.event.ticketFee,
          processingFee: eventData.event.processingFee,
          hasMultipleVenues: eventData.event.hasMultipleVenues,
          hasMultipleTicketGroups: eventData.event.hasMultipleTicketGroups,
          eventStatus: 'active'
        };
        localStorage.setItem('eventData', JSON.stringify(storedEventData));
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        navigate(`/event/${eventId}`);
      }, 5000);
      
    } catch (e: any) {
      console.error('Publish failed:', e);
      setError(e?.response?.data?.message || 'Failed to publish event');
    } finally {
      setIsPublishing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (error && !eventData) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Event</h4>
          <p className="mb-0">{error}</p>
        </div>
        <button className="btn btn-outline-secondary mt-3" onClick={() => (fromAskEv ? returnToAskEv() : navigate('/events'))}>
          {fromAskEv ? 'Back to Ask Ev' : 'Back to Events'}
        </button>
      </div>
    );
  }

  if (!eventData) return null;

  const isActive = eventData.event.eventStatus === 'active';

  return (
    <>
      <Background />
      <div className="create-event-container create-event-v2-page event-preview-page">
        <div className="container" style={{ maxWidth: 1200, margin: '0 auto' }}>
          <PageHeader
            title={isActive ? "Published Event Preview" : "Event Preview Studio"}
            subtitle={
              isActive
                ? "Your event is published and live."
                : "Review your event configuration before publishing."
            }
            icon={Eye}
            actions={
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {fromAskEv && (
                  <button
                    type="button"
                    className="v3-btn-secondary"
                    onClick={returnToAskEv}
                    style={{ fontSize: 13, padding: '8px 16px' }}
                  >
                    <ArrowLeft size={14} className="me-1" /> Back to Ask Ev
                  </button>
                )}
                <button
                  type="button"
                  className="v3-btn-secondary"
                  onClick={handleBackToEdit}
                  style={{ fontSize: 13, padding: '8px 16px' }}
                >
                  <Pencil size={14} className="me-1" /> Edit Event
                </button>

                <button
                  type="button"
                  className={`v3-btn-primary ${isActive ? 'disabled' : ''}`}
                  onClick={handlePublish}
                  disabled={isPublishing || isActive}
                  style={{ fontSize: 13, padding: '8px 20px' }}
                >
                  {isPublishing ? (
                    <>
                      <Spinner size="sm" color="light" className="me-2" />
                      Publishing...
                    </>
                  ) : isActive ? (
                    <>
                      <Check size={14} className="me-1" />
                      Published
                    </>
                  ) : (
                    <>
                      <Globe size={14} className="me-1" />
                      Publish Now
                    </>
                  )}
                </button>
              </div>
            }
          />

          <div className="row">
            {/* Main Preview Column */}
            <div className="col-lg-8">
              <div className="glass-panel preview-card mb-4">
                {/* Poster + Info */}
                <div className="preview-top-section">
                  <div className="preview-poster-wrapper">
                    <div className="preview-poster-column">
                      {eventData.event.bannerImgDesktop ? (
                        <img
                          src={eventData.event.bannerImgDesktop}
                          alt={eventData.event.eventName}
                          className="preview-poster-image"
                        />
                      ) : (
                        <div className="preview-poster-placeholder">
                          <ImageIcon size={40} className="text-muted" />
                        </div>
                      )}
                    </div>

                    <a
                      href={`https://eventox.co/event/${eventData.event.slugId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn view-event-btn w-100 mt-3 d-inline-flex align-items-center justify-content-center"
                      aria-label={`View event ${eventData.event.eventName} in new window`}
                    >
                      <span>View Event</span>
                      <ExternalLink size={14} className="ms-2" />
                    </a>
                  </div>

                  <div className="preview-info-column">
                    <h1 className="preview-event-name" id="event-title">
                      {eventData.event.eventName}
                    </h1>

                    <div className="mb-2 d-flex align-items-center flex-wrap gap-2">
                      <span className="d-flex align-items-center">
                        <CalendarIcon size={15} className="me-2 text-pink" style={{ color: '#EE3F7C' }} />
                        {getFormattedDateOnly()}
                      </span>
                      {getFormattedTimeOnly() && (  
                        <>
                          <span className="text-muted mx-2">|</span>
                          <span className="d-flex align-items-center">
                            <Clock size={15} className="me-2 text-pink" style={{ color: '#EE3F7C' }} />
                            {getFormattedTimeOnly()}
                          </span>
                        </>
                      )}
                    </div>

                    <div className=" mb-4 d-flex align-items-start">
                      <MapPin size={16} className="me-2 mt-1 flex-shrink-0" style={{ color: '#EE3F7C' }} />
                      <span className="address-text">
                        {eventData.venues && eventData.venues.length > 0 ? (
                          eventData.venues.map((v, i) => (
                            <div key={i} className={i > 0 ? "mt-1" : ""}>
                              <strong>{v.venueName}</strong>: {v.venueAddress}
                            </div>
                          ))
                        ) : (
                          eventData.event?.eventAddress1 || eventData.event?.eventLocation || 'Online Event'
                        )}
                      </span>
                    </div>

                    <div className="quick-stats-panel d-flex">
                      <div className="stat-column">
                        <span className="stat-label">
                          <Ticket size={14} style={{ color: '#EE3F7C' }} /> Ticket Price
                        </span>
                        <span className="stat-value">
                          {getTicketPriceText(eventData.ticketTypes)}
                        </span>
                      </div>
                      <div className="stat-column border-start border-end">
                        <span className="stat-label">
                          <Users size={14} style={{ color: '#EE3F7C' }} /> Max Attendees
                        </span>
                        <span className="stat-value">
                          {getTotalCapacity(eventData.ticketTypes)}
                        </span>
                      </div>
                      <div className="stat-column">
                        <span className="stat-label">
                          <Tag size={14} style={{ color: '#EE3F7C' }} /> Tickets
                        </span>
                        <span className="stat-value">
                          {eventData.ticketTypes?.length || 0} Ticket
                          {(eventData.ticketTypes?.length || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="preview-divider" />

                {eventData.event.eventDescription && (
                  <div className="about-event-section px-4 pb-4">
                    <h3 className="about-title">About this event</h3>
                    <div
                      className="about-description"
                      dangerouslySetInnerHTML={{
                        __html: eventData.event.eventDescription,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar Column */}
            <div className="col-lg-4">
              <div className="glass-panel event-details-sidebar">
                {/* Venues */}
                {eventData.event.hasMultipleVenues && eventData.venues && eventData.venues.length > 0 && (
                  <div className="detail-section mb-4">
                    <h3 className="section-title">
                      <i className="fa-solid fa-map-marker-alt me-2"></i>
                      Event Venues
                    </h3>
                    <div className="venues-preview">
                      {eventData.venues.map((venue, index) => {
                        const venueTickets = eventData.ticketTypes
                          ? eventData.ticketTypes.filter(
                              (ticket) => String(ticket.venueId) === String(venue.venueId)
                            )
                          : [];

                        return (
                          <div key={index} className="ticket-preview-card mb-3" style={{ border: '2px solid #6b7280' }}>
                            <div className="ticket-card-header">
                              <span className="ticket-card-name">{venue.venueName}</span>
                            </div>
                            <div className="mt-2 text-muted" style={{ fontSize: '12.5px', display: 'flex', alignItems: 'start', gap: '6px' }}>
                              <i className="fa-solid fa-location-dot mt-1"></i>
                              <span>{venue.venueAddress}</span>
                            </div>
                            <div className="mt-3 text-muted" style={{ fontSize: '12.5px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <i className="fa-regular fa-calendar-days"></i>
                              <span>{formatDate(venue.eventDate)}</span>
                            </div>

                            {venueTickets.length > 0 && (
                              <div className="mt-3 pt-3 border-top">
                                <div className="d-flex align-items-center mb-2" style={{ fontSize: '12px', fontWeight: 700, color: '#374151' }}>
                                  <i className="fa-solid fa-ticket me-1" style={{ color: '#d63384' }}></i>
                                  <span>Tickets</span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                  {venueTickets.map((ticket, tIndex) => (
                                    <div key={tIndex} className="venue-ticket-item d-flex justify-content-between align-items-center p-2" style={{ borderRadius: '8px', borderLeft: '3px solid #d63384' }}>
                                      <div>
                                        <div style={{ fontSize: '12.5px', fontWeight: 700 }}>{ticket.ticketType}</div>
                                        {eventData.event.hasMultipleTicketGroups && ticket.groupingId && getTicketGroupName(ticket.groupingId) && (
                                          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                                            <i className="fa-solid fa-layer-group me-1"></i>
                                            {getTicketGroupName(ticket.groupingId)}
                                          </div>
                                        )}
                                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                          {getTicketTypeText(ticket.isGroupTicket ? 'group' : 'single')} • Max: {getMaxCountText(ticket)}
                                        </div>
                                      </div>
                                      <div style={{ fontSize: '12.5px', fontWeight: 800 }}>
                                        {ticket.price === 0 ? "Free" : `${currencySymbol}${ticket.price.toFixed(2)}`}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Tickets */}
                {eventData.ticketTypes && eventData.ticketTypes.filter(ticket => !eventData.event.hasMultipleVenues || !ticket.venueId).length > 0 && (
                  <div className="detail-section mb-4">
                    <h3 className="sidebar-header-title d-flex align-items-center mb-3">
                      <i className="fa-solid fa-ticket me-2"></i>
                      Event Tickets
                    </h3>
                    <div className="tickets-preview">
                      {eventData.ticketTypes.filter(ticket => !eventData.event.hasMultipleVenues || !ticket.venueId).map((ticket, index) => (
                        <div key={index} className="ticket-preview-card mb-3">
                          <div className="ticket-card-header d-flex justify-content-between align-items-start">
                            <span className="ticket-card-name">{ticket.ticketType}</span>
                            <span className="ticket-type-badge-pill">
                              {getTicketTypeText(ticket.isGroupTicket ? 'group' : 'single')}
                            </span>
                          </div>
                          {eventData.event.hasMultipleTicketGroups && ticket.groupingId && getTicketGroupName(ticket.groupingId) && (
                            <div className="ticket-card-group mt-1" style={{ fontSize: '12px', color: '#888' }}>
                              <i className="fa-solid fa-layer-group me-1"></i>
                              {getTicketGroupName(ticket.groupingId)}
                            </div>
                          )}
                          {eventData.event.hasMultipleVenues && ticket.venueId && getVenueName(ticket.venueId) && (
                            <div className="ticket-card-venue mt-1" style={{ fontSize: '12px', color: '#888' }}>
                              <i className="fa-solid fa-location-dot me-1"></i>
                              {getVenueName(ticket.venueId)}
                            </div>
                          )}
                          <div className="ticket-card-body d-flex justify-content-between align-items-center mt-3">
                            <span className="ticket-card-price">
                              {ticket.price === 0 ? "Free" : `${currencySymbol}${ticket.price.toFixed(2)}`}
                            </span>
                            <span className="ticket-card-capacity text-muted">
                              <i className="fa-solid fa-users me-1"></i>
                              Max: {getMaxCountText(ticket)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sponsors */}
                {eventData.sponsors && eventData.sponsors.length > 0 && (
                  <div className="detail-section mb-4">
                    <h3 className="section-title">Event Sponsors</h3>
                    <div className="sponsors-preview">
                      {(showAllSponsors ? eventData.sponsors : eventData.sponsors.slice(0, 3)).map((sponsor, index) => (
                        <div key={index} className="sponsor-item">
                          <div className="sponsor-name">{sponsor.name}</div>
                          <div className="sponsor-type">
                            Sponsor Type: {getSponsorTypeTitle(sponsor.sponsorTypeId)}
                          </div>
                        </div>
                      ))}
                      {eventData.sponsors.length > 3 && (
                        <div className="sponsors-more" onClick={() => setShowAllSponsors(prev => !prev)}>
                          {showAllSponsors ? "Show less" : `+${eventData.sponsors.length - 3} more sponsors`}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Agenda */}
                {eventData.agendas && eventData.agendas.length > 0 && (
                  <div className="detail-section mb-4">
                    <h3 className="section-title">Event Agenda</h3>
                    <div className="agenda-preview">
                      {(showAllAgendas ? eventData.agendas : eventData.agendas.slice(0, 3)).map((item, index) => (
                        <div key={index} className="agenda-item">
                          <div className="agenda-time">
                            {item.startTime} - {item.endTime}
                          </div>
                          <div className="agenda-title">{item.title}</div>
                          <div className="agenda-host">
                            Host: {item.hostName}
                          </div>
                        </div>
                      ))}
                      {eventData.agendas.length > 3 && (
                        <div className="agenda-more" onClick={() => setShowAllAgendas(prev => !prev)}>
                          {showAllAgendas ? "Show less" : `+${eventData.agendas.length - 3} more agenda items`}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Lineup */}
                {eventData.lineups && eventData.lineups.length > 0 && (
                  <div className="detail-section mb-4">
                    <h3 className="section-title">Event Lineup</h3>
                    <div className="lineup-preview">
                      {(showAllLineups ? eventData.lineups : eventData.lineups.slice(0, 3)).map((item, index) => (
                        <div key={index} className="lineup-item">
                          <div className="lineup-name">{item.name}</div>
                          <div className="lineup-tagline">{item.tagLine}</div>
                        </div>
                      ))}
                      {eventData.lineups.length > 3 && (
                        <div className="lineup-more" onClick={() => setShowAllLineups(prev => !prev)}>
                          {showAllLineups ? "Show less" : `+${eventData.lineups.length - 3} more lineup items`}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EventPreviewPage;
